import { FC, HTMLAttributes } from 'react'

import { EmbroideryThread, Warning } from '@syconium/little-miss-figgy'

import { ProductCatalogEmbroideryPlacement } from '../../../__generated__/graphql/catalog/graphql'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { deriveNameFromEmbroideryPlacement } from '../../../containers/EmbroideryContainer/utils/deriveNameFromEmbroideryPlacement'
import { useEmbroideryColorFeedback } from '../useEmbroideryColorFeedback'
import { getEmbroideryNudgedColor } from '../utils'

import { ColorFeedbackLine, DetailLine, Icon, IconEmbroideryVisual, Initials } from './styles'
import { Template } from './Template'

export interface Props extends HTMLAttributes<HTMLDivElement> {
  backgroundColor: string | undefined
  iconImageSource: string | undefined
  iconInitials: string | undefined
  iconInitialsColorCss: string | undefined
  iconInitialsColorDisplayName: string | undefined
  iconDesignName: string | undefined
  iconPlacement: ProductCatalogEmbroideryPlacement | string | undefined
}

export const IconSneakPeek: FC<Props> = ({
  backgroundColor,
  iconImageSource,
  iconInitials,
  iconInitialsColorCss,
  iconInitialsColorDisplayName,
  iconDesignName,
  iconPlacement,
  ...rest
}) => {
  const placementString =
    typeof iconPlacement === 'undefined'
      ? undefined
      : typeof iconPlacement === 'string'
      ? iconPlacement
      : deriveNameFromEmbroideryPlacement(iconPlacement)

  const previewInitialsColor = getEmbroideryNudgedColor(backgroundColor, iconInitialsColorCss)
  const colorFeedback = useEmbroideryColorFeedback({
    backgroundColor,
    threadColor: iconInitialsColorCss,
  })

  const {
    embroidery: {
      summary: {
        iconNameLabel,
        iconInitialsColorLabelLine,
        iconInitialsLabelLine,
        iconPlacementLabelLine,
      },
    },
  } = useTranslation()
  const iconInitialsColorLabelFixture = iconInitialsColorLabelLine.replace(
    '{{iconInitialsColor}}',
    iconInitialsColorDisplayName ?? ''
  )
  const iconInitialsLabelFixture = iconInitialsLabelLine.replace(
    '{{iconInitials}}',
    iconInitials ?? ''
  )
  const iconPlacementLabelFixture = iconPlacementLabelLine.replace(
    '{{iconPlacement}}',
    placementString ?? ''
  )

  const displayIconNameTitle = iconInitials || iconPlacement

  if (!iconPlacement && !iconImageSource) return null

  return (
    <Template.Container {...rest}>
      <Template.Header backgroundColor={backgroundColor}>
        {iconImageSource && (
          <IconEmbroideryVisual>
            <Icon
              alt={iconDesignName ?? 'Embroidery Icon Sneak Peak'}
              src={iconImageSource}
              widths={{ unit: 'px', sm: 36, md: 36 }}
              aspectRatios={{ sm: null, md: null }}
              loading='default'
              transparentBackground={true}
            />

            {iconInitials && (
              <Initials $initialsColor={previewInitialsColor?.toLowerCase()}>
                {iconInitials}
              </Initials>
            )}
          </IconEmbroideryVisual>
        )}
      </Template.Header>

      <Template.Body>
        {colorFeedback && (
          <ColorFeedbackLine
            role={colorFeedback.tone === 'caution' ? 'alert' : 'status'}
            $tone={colorFeedback.tone}
          >
            {colorFeedback.message}
            {colorFeedback.icon === 'thread' ? (
              <EmbroideryThread height={'16px'} width={'16px'} />
            ) : (
              <Warning height={'16px'} width={'16px'} />
            )}
          </ColorFeedbackLine>
        )}
        {iconDesignName && (
          <DetailLine $general>
            {displayIconNameTitle ? `${iconNameLabel}: ` : ''}
            {iconDesignName}
          </DetailLine>
        )}
        {iconInitials && <DetailLine $general>{iconInitialsLabelFixture}</DetailLine>}
        {iconInitials && iconInitialsColorDisplayName && (
          <DetailLine>{iconInitialsColorLabelFixture}</DetailLine>
        )}
        {iconPlacement && <DetailLine>{iconPlacementLabelFixture}</DetailLine>}
      </Template.Body>
    </Template.Container>
  )
}
